/**
 * VideosTab — "Video tutorial" Formazione: player integrato con streaming via
 * ticket, upload chunked (max 300MB) e documenti di supporto accanto ai video.
 * Visibile a tutti i dipendenti; carica chi gestisce la formazione.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { trainingApi, BACKEND_URL } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Clapperboard, FileText, Loader2, PlayCircle, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import UploadFileDialog from './UploadFileDialog';
import UploadVideoDialog from './UploadVideoDialog';

const fmtSize = (n) => n ? (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`) : '';
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('it-IT') : '—';

export default function VideosTab({ meta }) {
  const { user } = useAuth();
  const isOps = user?.role === 'operations_manager';
  const [videos, setVideos] = useState(null);
  const [docs, setDocs] = useState([]);
  const [showUploadVideo, setShowUploadVideo] = useState(false);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [playing, setPlaying] = useState({}); // file_id → stream url
  const [openingId, setOpeningId] = useState(null);

  const load = useCallback(() => {
    Promise.all([trainingApi.files('video'), trainingApi.files('video_doc')])
      .then(([v, d]) => { setVideos(v.data); setDocs(d.data); })
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento video'); setVideos([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const play = async (f) => {
    try {
      const r = await trainingApi.videoTicket(f.file_id);
      setPlaying(p => ({ ...p, [f.file_id]: `${BACKEND_URL}${r.data.url}` }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore avvio video');
    }
  };

  const doDelete = async (f, label) => {
    if (!window.confirm(`Eliminare ${label} "${f.title}"?`)) return;
    try {
      await trainingApi.deleteFile(f.file_id);
      toast.success('Eliminato');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eliminazione fallita'); }
  };

  const openDoc = async (f) => {
    setOpeningId(f.file_id);
    const win = window.open('', '_blank');
    try {
      const r = await trainingApi.downloadFile(f.file_id);
      const url = URL.createObjectURL(new Blob([r.data], { type: f.content_type || 'application/pdf' }));
      if (win && !win.closed) win.location = url; else window.open(url, '_blank');
    } catch {
      if (win && !win.closed) win.close();
      toast.error('Errore apertura file');
    } finally {
      setOpeningId(null);
    }
  };

  if (videos === null) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div data-testid="videos-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-slate-500">
          {videos.length} video{docs.length > 0 ? ` · ${docs.length} documenti di supporto` : ''}
        </p>
        {meta.can_manage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowUploadDoc(true)} className="gap-1.5"
                    data-testid="upload-video-doc-btn">
              <FileText className="w-4 h-4" /> Carica documento
            </Button>
            <Button onClick={() => setShowUploadVideo(true)} className="gap-1.5" data-testid="upload-video-btn">
              <Upload className="w-4 h-4" /> Carica video
            </Button>
          </div>
        )}
      </div>

      {videos.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400" data-testid="videos-empty">
          <Clapperboard className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>Nessun video tutorial ancora{meta.can_manage ? ' — carica il primo!' : '.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {videos.map(f => (
            <div key={f.file_id} className="bg-white border border-slate-200 flex flex-col" data-testid={`video-card-${f.file_id}`}>
              <div className="aspect-video bg-slate-900 relative">
                {playing[f.file_id] ? (
                  <video controls autoPlay playsInline className="w-full h-full"
                         src={playing[f.file_id]} data-testid={`video-player-${f.file_id}`} />
                ) : (
                  <button type="button" onClick={() => play(f)}
                          className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white hover:bg-white/5 transition-colors"
                          data-testid={`video-play-${f.file_id}`}>
                    <PlayCircle className="w-14 h-14 opacity-90" />
                    <span className="text-xs text-slate-300">Riproduci</span>
                  </button>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col gap-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900">{f.title}</p>
                  {isOps && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 shrink-0"
                            onClick={() => doDelete(f, 'il video')} data-testid={`video-delete-${f.file_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                {f.description && <p className="text-xs text-slate-500 leading-snug">{f.description}</p>}
                <p className="text-[11px] text-slate-400 mt-auto">{fmtSize(f.size)} · caricato il {fmtDate(f.uploaded_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {docs.length > 0 && (
        <div className="mt-6">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">Documenti di supporto</h3>
          <div className="bg-white border border-slate-200 divide-y divide-slate-100">
            {docs.map(f => (
              <div key={f.file_id} className="flex items-center gap-3 px-4 py-3 flex-wrap" data-testid={`video-doc-row-${f.file_id}`}>
                <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-medium text-slate-900">{f.title}</p>
                  <p className="text-[11px] text-slate-400">{f.filename} · {fmtSize(f.size)} · caricato il {fmtDate(f.uploaded_at)}</p>
                </div>
                <div className="flex gap-1.5">
                  {isOps && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600"
                            onClick={() => doDelete(f, 'il documento')} data-testid={`video-doc-delete-${f.file_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="h-8 rounded-none gap-1.5"
                          onClick={() => openDoc(f)} disabled={openingId === f.file_id}
                          data-testid={`video-doc-open-${f.file_id}`}>
                    {openingId === f.file_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Apri
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showUploadVideo && (
        <UploadVideoDialog onClose={() => setShowUploadVideo(false)}
                           onUploaded={() => { setShowUploadVideo(false); load(); }} />
      )}
      {showUploadDoc && (
        <UploadFileDialog kind="video_doc" onClose={() => setShowUploadDoc(false)}
                          onUploaded={() => { setShowUploadDoc(false); load(); }} />
      )}
    </div>
  );
}
